import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { signout } from '~/components/authentication/ducks/index.js';
import { showPanicReason } from '~/components/account/reality-check/ducks/index.js';
import { selectCoolOffPeriodOn, selectCoolOffTimeStamp } from '~/components/account/reality-check/ducks/selectors.js';
import Icon, { ICON_LOCK_CLOSED_LINE, ICON_CHECKMARK } from '~/components/icon/index.js';
import breakpoint, { getZindexByType } from '../../../utils/style-utils';
import { showNotification } from '../../notifications/ducks/notifications.js';
import { postJson } from '~/core/ajax-json';
import CoolOffTimer from '../cool-off-timer';
import useTranslations from '../../translation/use-translations';
import { selectIsDesktop, selectGermanProductSwitchEnabled } from '../../brand/ducks/selectors.js';
import ReactSwipeButton from '@comeon/ui/src/buttons/react-swipe-button';
import { WEB_EVENTS, APP_EVENTS, sendAppBridgeEvent, registerAppEventCallback } from '~/components/app-bridge/index.js';
import { isNativeRegHeaderEnabled } from '~/components/app-bridge/app-properties.js';
import { LICENSE_DEGA } from '~/utils/license-utils';
import getRootElement from '~/utils/get-root-element.js';
import { Smaller, Bigger } from '~/components/layout/mediaquery.js';
import ActiveProducts from '~/components/account/active-products';
import { selectProductSwitchState } from '~/components/account/product-switch/product-switch-duck';
import IconText from '~/components/footer/footer-logo-text';
import { Box } from '@comeon/ui';

const rootElement = getRootElement();

const Wrapper = styled.div`
	background-color: rgba(var(--base-10), 1);
	display: flex;
	justify-content: normal;
	align-items: center;
	width: 100%;
	max-width: 100vw;
	height: var(--regulationHeight);
	z-index: ${getZindexByType('regulations-header')};
`;

const Center = styled.div`
	display: flex;
	position: absolute;
	left: 50%;
	top: 50%;
	align-items: center;
	transform: translate(-50%, -50%);
`;

const GermanRegulationWrapper = styled.div`
	display: flex;
	width: 100%;
	align-items: center;
	justify-content: space-around;
	${breakpoint.md`
		margin-left: 20px;
	`}
`;

const PanicButtonWrapper = styled.div`
	display: flex;
	justify-content: space-evenly;
	align-items: center;
	background-color: var(--panic-wrapper-bg, rgba(var(--base-11), 1));
	border-radius: 27px;
	width: ${props => (props.panicButtonContainerWidth ? `${props.panicButtonContainerWidth}px` : '190px')};
	height: 40px;
	${breakpoint.lg`
		width: ${props => (props.panicButtonContainerWidth ? `${props.panicButtonContainerWidth}px` : '550px')};
		height: 60px;
		justify-content: center;
		min-width: 300px
	`}
`;

const SwipeWrapper = styled.span`
	width: 73px;
	${breakpoint.lg`
		width: 80px;
		height: 40px;
	`}
`;

const ActiveProductsWrapper = styled.span`
	position: absolute;
	right: 0;
`;

const ICONLINE_STYLE = {
	stroke: 'rgba(var(--base-01), 1)'
};
const TEXT_KEYS = ['PANIC_BUTTON_TEXT', 'PANIC_BUTTON_TOOLTIP_TEXT', 'PRODUCT_SWITCH_ALLOW_BET_NOTIFICATION'];

const RegulationsHeaderDEGA = () => {
	const dispatch = useDispatch();

	const panicConfirm = useCallback(() => {
		postJson(`/player-account/close`, {
			key: 'ACCOUNT_CLOSING_REASON_RG_TEMP_TIMEOUT',
			value: {
				oasisReason: 'RES_GAMING_CLOSE_24HOURS'
			}
		})
			.then(response => {
				if (response.status === 'SUCCESS') {
					const token = response.result;
					dispatch(signout({ message: '' }));
					dispatch(showPanicReason(token));
				} else {
					dispatch(
						showNotification({
							message: response.errorDescription,
							color: 'red',
							autoHide: true,
							openInSidebar: false
						})
					);
				}
			})
			.catch(reason => console.error(reason)); //eslint-disable-line
	}, [dispatch]);

	const authenticated = useSelector(authenticationSelector);
	const coolOffPeriodOn = useSelector(selectCoolOffPeriodOn);
	const coolOffTimeStamp = useSelector(selectCoolOffTimeStamp);
	const isDesktop = useSelector(selectIsDesktop);
	const isGermanProductSwitchEnabled = useSelector(selectGermanProductSwitchEnabled);
	const { translations, isLoadingTranslations } = useTranslations(TEXT_KEYS);
	const [panicButtonContainerWidth, setPanicButtonContainerWidth] = useState(0);
	const { activeProduct, timestamp } = useSelector(selectProductSwitchState);

	// MOBILE APP RELATED EVENTS
	useEffect(() => {
		registerAppEventCallback(APP_EVENTS.INVOKE_PANIC_CONFIRM, panicConfirm);
	}, [panicConfirm]);

	useEffect(() => {
		const payload = {
			coolOffPeriodOn,
			coolOffTimeStamp,
			productSwitch: isGermanProductSwitchEnabled && {
				activeProduct,
				timestamp
			}
		};
		sendAppBridgeEvent(WEB_EVENTS.ON_HEADER_UPDATE, [
			{
				license: LICENSE_DEGA,
				payload
			}
		]);
	}, [coolOffPeriodOn, coolOffTimeStamp, isGermanProductSwitchEnabled, activeProduct, timestamp]);

	// overriding global css variable to accommodate new Panic Button BANJO-16652
	// to do, maybe move to constants or to an ETCD property
	useEffect(() => {
		const r = document.querySelector(':root');
		r.style.setProperty('--regulationHeight', `${isDesktop ? 84 : 62}px`);
	}, [isDesktop]);

	// calculate panic-button container width to use min 2.5% of viewport
	// MOBILE APP RELATED EVENTS
	useEffect(() => {
		const viewportArea = window.innerWidth * window.innerHeight;
		const minAreaPanicButton = viewportArea * 0.026; // using 2.6% to reduce edge cases
		let minWidthPanicButtonContainer = Math.round(minAreaPanicButton / (isDesktop ? 60 : 40)); // 40 and 60 Figma fixed height of slider
		minWidthPanicButtonContainer = minWidthPanicButtonContainer >= 202 ? minWidthPanicButtonContainer : 202;
		setPanicButtonContainerWidth(minWidthPanicButtonContainer);
	}, [isDesktop]);

	const show = !isLoadingTranslations && !isNativeRegHeaderEnabled();
	const showProductSwitchTimer = timestamp > 0 && !coolOffPeriodOn && isGermanProductSwitchEnabled;

	return (
		show && (
			<Wrapper
				id="regulations-header"
				data-at="regulation-header-wrapper"
				className="reg-header-wrapper reg-head-wrapper-bg"
			>
				{authenticated && (
					<GermanRegulationWrapper>
						{showProductSwitchTimer && (
							<ActiveProductsWrapper>
								<ActiveProducts
									productSwitchPopup
									timer={timestamp}
									product={activeProduct}
									onEndMessage={translations.PRODUCT_SWITCH_ALLOW_BET_NOTIFICATION}
								/>
							</ActiveProductsWrapper>
						)}
						<>
							<Smaller>
								<Center>
									<PanicButtonWrapper
										id="panic-button-wrapper"
										title={translations.PANIC_BUTTON_TOOLTIP_TEXT}
										panicButtonContainerWidth={panicButtonContainerWidth}
									>
										<SwipeWrapper>
											<ReactSwipeButton
												dataAt="regulation-header-panic-button"
												rootElement={rootElement}
												isSmall
												buttonIcon={
													<Icon type={ICON_LOCK_CLOSED_LINE} style={ICONLINE_STYLE} />
												}
												confirmIcon={<Icon type={ICON_CHECKMARK} />}
												onConfirm={panicConfirm}
											/>
										</SwipeWrapper>
										<span className="panic-btn_txt">{translations.PANIC_BUTTON_TEXT}</span>
									</PanicButtonWrapper>
									<Box display="flex" height="100%" alignItems="center">
										{coolOffPeriodOn && (
											<CoolOffTimer
												coolOffTimeStamp={coolOffTimeStamp}
												className="header-rc-coolOff"
												inHeader
											/>
										)}
										<IconText
											size="36px"
											marginLeft="12px"
											top={0}
											transform="translateY(-50%)"
											height="100%"
											smaller
											disablePopup
											item={{ name: '18+', url: 'LICENSE_AGE_LIMIT_TEXT' }}
										/>
									</Box>
								</Center>
							</Smaller>
							<Bigger>
								<Center>
									<PanicButtonWrapper
										id="panic-button-wrapper"
										title={translations.PANIC_BUTTON_TOOLTIP_TEXT}
										panicButtonContainerWidth={panicButtonContainerWidth}
									>
										<SwipeWrapper>
											<ReactSwipeButton
												dataAt="regulation-header-panic-button"
												rootElement={rootElement}
												buttonIcon={
													<Icon
														type={ICON_LOCK_CLOSED_LINE}
														height="18"
														width="18"
														style={ICONLINE_STYLE}
													/>
												}
												confirmIcon={<Icon type={ICON_CHECKMARK} height="18" width="18" />}
												onConfirm={panicConfirm}
												onFailure={() => {}}
											/>
										</SwipeWrapper>
										<span className="panic-btn_txt">{translations.PANIC_BUTTON_TEXT}</span>
									</PanicButtonWrapper>
								</Center>
								<Box display="flex" height="100%" alignItems="center" position="absolute" right="24px">
									{coolOffPeriodOn && (
										<CoolOffTimer
											coolOffTimeStamp={coolOffTimeStamp}
											className="header-rc-coolOff"
											inHeader
										/>
									)}
									<IconText
										size="36px"
										marginLeft="12px"
										top={0}
										transform="translateY(-50%)"
										disablePopup
										smaller
										height="100%"
										item={{ name: '18+', url: 'LICENSE_AGE_LIMIT_TEXT' }}
									/>
								</Box>
							</Bigger>
						</>
					</GermanRegulationWrapper>
				)}
			</Wrapper>
		)
	);
};

export default RegulationsHeaderDEGA;
